// =============================================================================
// io/sync/dismissSetupNudge.ts — the ✕ on a card's "N fields missing" prompt.
// -----------------------------------------------------------------------------
// ── 🚨 IT WRITES ONE KEY INTO `extras`, AND THAT IS THE WHOLE DANGER ──
//
// `extras` is a single JSONB blob holding `excludeFromMetrics`, `archived`, the
// three cost fields, `rulesX`, and anything the shipping client has written that
// this one has never heard of. An update built as `{ extras: { setupNudgeDismissed
// : true } }` does not set a flag — it REPLACES the blob and deletes the rest:
// the archived state, the cost of the account, the consistency scope. That is
// D110's shape exactly, and `saveAccount` exists in the form it does because of
// it.
//
// So this re-reads the blob, spreads it, and changes one key.
//
// ⚠ AND IT RE-READS AT WRITE TIME, not when the card rendered. The card may have
// been on screen for an hour while an import, the realtime ingest or the other
// client wrote to the same row.
//
// ⚠ IT IS A SEPARATE WRITER FROM `saveAccount` ON PURPOSE. The edit form does
// not own this field, and adding it to `ownedAccountFields` would mean every
// save of the form sent a value for it — D62: never send a field you are not
// deliberately writing.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailure } from '../diagnostics/errorLog';

export interface DismissResult {
  ok: boolean;
  error: string | null;
}

export async function dismissSetupNudge(
  sb: SupabaseClient, accountId: string, dismissed = true,
): Promise<DismissResult> {
  if (!accountId) return { ok: false, error: 'Refusing to write with no account id.' };

  const { data, error: readError } = await sb
    .from('accounts')
    .select('extras')
    .eq('id', accountId)
    .maybeSingle();

  if (readError) {
    return { ok: false, error: reportFailure('dismiss the setup prompt', readError) };
  }
  // ⚠ A row RLS hides comes back as `null`, not as an error, and an UPDATE
  // against it SUCCEEDS with an empty result. Refusing here is what turns a
  // silent no-op into a visible failure — the same reason `saveAccount` pre-reads.
  if (!data) {
    return {
      ok: false,
      error: 'That account could not be found, so nothing was changed.',
    };
  }

  const stored = ((data as Record<string, unknown>)['extras'] ?? {}) as Record<string, unknown>;

  const { error } = await sb
    .from('accounts')
    .update({ extras: { ...stored, setupNudgeDismissed: dismissed } })
    .eq('id', accountId);

  if (error) {
    return { ok: false, error: reportFailure('dismiss the setup prompt', error) };
  }
  return { ok: true, error: null };
}
